import React, { useRef, useEffect, useState } from "react";
import { useLanguage } from "../context/LanguageContext";

const VIDEO_URLS = {
  strategy: "https://customer-assets.emergentagent.com/job_9a8ede33-8bce-4373-9ea5-e19fcc2bf03e/artifacts/da3z5k56_strategy.mp4",
  contentCreation: "https://customer-assets.emergentagent.com/job_9a8ede33-8bce-4373-9ea5-e19fcc2bf03e/artifacts/gpecksto_content%20creation.mp4",
  campaigns: "https://customer-assets.emergentagent.com/job_9a8ede33-8bce-4373-9ea5-e19fcc2bf03e/artifacts/71o0oyqz_campaigns%20%282%29.mp4",
  webAutomation: "https://customer-assets.emergentagent.com/job_9a8ede33-8bce-4373-9ea5-e19fcc2bf03e/artifacts/e01169v8_web%20automation%20%281%29.mp4",
};

const NAV_IDS = {
  strategy: "strategy",
  contentCreation: "content-creation",
  campaigns: "campaigns",
  webAutomation: "web-automation",
};

const VIDEO_LEFT = { strategy: true, contentCreation: false, campaigns: true, webAutomation: false };

const RICH_DESCRIPTIONS_EN = {
  strategy: <>We define the <strong>direction</strong> before the brand starts moving. Analysing <strong>context</strong>, positioning, identity, audience, competitors, and opportunities — then turning all of it into a <strong>clearer point of view</strong> and a more solid <strong>decision-making framework</strong>.</>,
  contentCreation: <>We turn the brand into something people can actually <strong>see, hear</strong>, and <strong>recognise</strong>. From art direction and copy to visual assets, social formats, and editorial systems — creating content that feels <strong>coherent</strong>, <strong>distinctive</strong>, and consistently <strong>on-brand</strong>.</>,
  campaigns: <>We build campaigns with a <strong>clear objective</strong> and a <strong>clear voice</strong>. Concept development, messaging, creative direction, launch planning, and <strong>performance thinking</strong> — so the brand can <strong>reach people effectively</strong>.</>,
  webAutomation: <>We design digital systems that make the brand <strong>easier to run</strong>. From <strong>automated flows</strong> and lead handling to website logic, customer journeys, and connected tools — the focus is on <strong>reducing friction</strong>, saving time, and <strong>improving what works behind the scenes</strong>.</>,
};

const LazyVideo = ({ src, inView: parentInView }) => {
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const [shouldLoad, setShouldLoad] = useState(false);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShouldLoad(true);
          observer.disconnect();
        }
      },
      { rootMargin: "200px" }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (shouldLoad && videoRef.current) {
      videoRef.current.play().catch(() => {});
    }
  }, [shouldLoad]);

  return (
    <div ref={containerRef} style={{ width: "100%", height: "100%", background: "#e8e8e8" }}>
      {shouldLoad && (
        <video
          ref={videoRef}
          autoPlay muted loop playsInline
          preload="metadata"
          style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
        >
          <source src={src} type="video/mp4" />
        </video>
      )}
    </div>
  );
};

const ServiceSection = ({ type }) => {
  const { t, lang } = useLanguage();
  const section = t.sections[type];
  const videoLeft = VIDEO_LEFT[type];
  const navId = NAV_IDS[type];

  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setInView(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const videoEl = (
    <div style={{
      flex: isMobile ? "none" : "1.4 1 0",
      overflow: "hidden",
      aspectRatio: "16/10",
      transform: inView ? "translateY(0)" : "translateY(40px)",
      opacity: inView ? 1 : 0,
      transition: `opacity 0.7s ease ${videoLeft ? "0.1s" : "0.25s"}, transform 0.7s ease ${videoLeft ? "0.1s" : "0.25s"}`,
      minWidth: isMobile ? "100%" : "auto",
    }}>
      <LazyVideo src={VIDEO_URLS[type]} inView={inView} />
    </div>
  );

  const textEl = (
    <div style={{
      flex: "1 1 0",
      display: "flex", flexDirection: "column", justifyContent: "flex-start",
      padding: isMobile ? "24px 0 0 0" : videoLeft ? "0 0 0 48px" : "0 48px 0 0",
      transform: inView ? "translateY(0)" : "translateY(40px)",
      opacity: inView ? 1 : 0,
      transition: `opacity 0.7s ease ${videoLeft ? "0.25s" : "0.1s"}, transform 0.7s ease ${videoLeft ? "0.25s" : "0.1s"}`,
      minWidth: isMobile ? "100%" : "auto",
    }}>
      <div style={{
        width: "90%",
        height: "2px",
        background: "#000",
        marginBottom: "24px",
      }} />
      <h2
        style={{
          fontFamily: "'Archivo Black', 'Arial Black', sans-serif",
          fontWeight: 900,
          fontSize: isMobile ? "26px" : "clamp(22px, 2.8vw, 36px)",
          color: "#333",
          margin: "0 0 24px 0",
          letterSpacing: "-0.5px", lineHeight: 1.05,
        }}
      >
        {section.title}
      </h2>
      <p style={{
        fontFamily: "'Poppins', sans-serif",
        fontSize: "15px",
        color: "#555",
        lineHeight: 1.85,
        margin: 0,
        maxWidth: "480px",
      }}>
        {lang === "en" ? RICH_DESCRIPTIONS_EN[type] : section.text}
      </p>
    </div>
  );

  return (
    <section
      id={navId}
      ref={ref}
      data-testid={`service-section-${type}`}
      style={{ padding: isMobile ? "40px 24px" : "50px 48px", background: "#fafafa" }}
    >
      <div style={{
        maxWidth: "1200px", margin: "0 auto",
        display: "flex",
        gap: isMobile ? "0" : "48px",
        alignItems: "flex-start",
        flexDirection: isMobile ? "column" : (videoLeft ? "row" : "row-reverse"),
        flexWrap: "wrap",
      }}>
        {videoEl}
        {textEl}
      </div>
    </section>
  );
};

export default ServiceSection;
